import React, { useEffect, useState } from 'react';
import Card from 'components/card';
import axios from 'axios';
import { CONFIG } from 'variables/config';
import { useNavigate, useParams } from 'react-router-dom';
import { MdArrowBack, MdSave, MdOpenInNew, MdSearch, MdAccountBalanceWallet, MdBlock, MdLockOpen, MdLanguage, MdVpnKey } from 'react-icons/md';
import { allChannels } from 'variables/industries';

interface Coupon {
    _id: string;
    coupon: string;
    type: string;
    fixedValue: number;
}

interface UserFormData {
    firstName: string;
    lastName: string;
    email: string;
    phone?: string;
    type: 'buyer' | 'seller';
    role?: 'admin' | '';
    company?: string;
    website?: string;
    address?: string;
    vatNumber?: string;
    siren?: string;
    siret?: string;
    isValidated?: boolean;
    isKYCVerified?: boolean;
    forceKYCVerification?: boolean;
    canSellWithGSheet?: boolean;
    blockedSeller?: boolean;
    canSwitchAccountType?: boolean;
    referal?: string;
    commission?: number;
    commissionFixedPrice?: number | null;
    prePaidBalance?: number;
    alreadyPaidAmount?: number;
    paymentMethod?: 'stripe' | 'cagnotte';
    channels?: string[];
    // '' => use the platform-wide pixel mode set in Back Office > Pixels.
    pixelModeOverride?: '' | 'mandatory' | 'optional' | 'hidden';
}

export default function UserForm() {
    const { id } = useParams();
    const navigate = useNavigate();

    const [formData, setFormData] = useState<UserFormData>({
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        type: 'buyer',
        role: '',
        company: '',
        website: '',
        address: '',
        vatNumber: '',
        siren: '',
        siret: '',
        isValidated: false,
        isKYCVerified: false,
        forceKYCVerification: false,
        canSellWithGSheet: false,
        blockedSeller: false,
        canSwitchAccountType: false,
        referal: '',
        commission: 15,
        commissionFixedPrice: null,
        prePaidBalance: 0,
        alreadyPaidAmount: 0,
        paymentMethod: 'stripe',
        channels: [],
        pixelModeOverride: '',
    });

    const [stripeAccountId, setStripeAccountId] = useState('');
    const [cagnotteBalance, setCagnotteBalance] = useState<number>(0);
    const [cagnotteTransactions, setCagnotteTransactions] = useState<any[]>([]);
    const [coupons, setCoupons] = useState<Coupon[]>([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');
    const [walletBalance, setWalletBalance] = useState<number>(0);
    const [walletTransactions, setWalletTransactions] = useState<any[]>([]);
    const [walletWithdrawalRequests, setWalletWithdrawalRequests] = useState<any[]>([]);
    const [walletOwnerEmail, setWalletOwnerEmail] = useState<string>('');
    const [linkedAccount, setLinkedAccount] = useState<{ email: string; type: string } | null>(null);
    const [walletAmount, setWalletAmount] = useState<string>('');
    const [walletReason, setWalletReason] = useState<string>('');
    const [walletSaving, setWalletSaving] = useState(false);
    const [walletError, setWalletError] = useState('');
    const [walletSuccess, setWalletSuccess] = useState('');
    const [userType, setUserType] = useState<string>('buyer');
    const [isBlocked, setIsBlocked] = useState(false);
    const [isLoginLocked, setIsLoginLocked] = useState(false);
    const [blockSaving, setBlockSaving] = useState(false);
    const [unlockSaving, setUnlockSaving] = useState(false);
    const [blockError, setBlockError] = useState('');
    const [blockSuccess, setBlockSuccess] = useState('');
    const [showResetPasswordConfirm, setShowResetPasswordConfirm] = useState(false);
    const [resetPasswordSaving, setResetPasswordSaving] = useState(false);
    const [resetPasswordError, setResetPasswordError] = useState('');
    const [resetPasswordSuccess, setResetPasswordSuccess] = useState('');
    const [ipHistory, setIpHistory] = useState<{ ip: string; country: string; firstSeenAt: string }[]>([]);
    const [blockedIps, setBlockedIps] = useState<Set<string>>(new Set());
    const [ipActionLoading, setIpActionLoading] = useState<Set<string>>(new Set());

    useEffect(() => {
        fetchUser();
        fetchCoupons();
        fetchBlockedIps();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id]);

    const fetchCoupons = async () => {
        try {
            const token = localStorage.getItem('token');
            const response = await axios.get(`${CONFIG.apiUrl}/admin/coupons`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            setCoupons(response.data);
        } catch (error) {
            console.error('Error fetching coupons:', error);
        }
    };

    const fetchUser = async () => {
        try {
            setLoading(true);
            const token = localStorage.getItem('token');
            const response = await axios.get(`${CONFIG.apiUrl}/admin/users/${id}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            
            const user = response.data;
            setFormData({
                firstName: user.name || '',
                lastName: user.surname || '',
                email: user.email || '',
                phone: user.phone || '',
                type: user.type || 'buyer',
                role: user.role || '',
                company: user.company || '',
                website: user.website || '',
                address: user.address || '',
                vatNumber: user.vatNumber || '',
                siren: user.siren || '',
                siret: user.siret || '',
                isValidated: user.isValidated || false,
                isKYCVerified: user.isKYCVerified || false,
                forceKYCVerification: user.forceKYCVerification || false,
                canSellWithGSheet: user.canSellWithGSheet || false,
                blockedSeller: user.blockedSeller || false,
                canSwitchAccountType: user.canSwitchAccountType || false,
                referal: user.referal || '',
                commission: user.commission ?? 15,
                commissionFixedPrice: user.commissionFixedPrice ?? null,
                prePaidBalance: user.prePaidBalance ?? 0,
                alreadyPaidAmount: user.alreadyPaidAmount ?? 0,
                paymentMethod: user.paymentMethod ?? 'stripe',
                channels: user.channels ?? [],
                pixelModeOverride: user.pixelModeOverride ?? '',
            });
            setLinkedAccount(
                user.linkedAccountId && typeof user.linkedAccountId === 'object'
                    ? { email: user.linkedAccountId.email, type: user.linkedAccountId.type }
                    : null
            );
            setStripeAccountId(user.stripeAccountId || '');
            setCagnotteBalance(user.cagnotteBalance || 0);
            setCagnotteTransactions((user.cagnotteTransactions || []).slice().reverse());
            setUserType(user.type || 'buyer');
            setIsBlocked(user.isBlocked || false);
            setIsLoginLocked(user.loginLockedByAdmin || false);
            setIpHistory(user.ipHistory || []);
        } catch (error: any) {
            console.error('Error fetching user:', error);
            setError(error.response?.data?.message || 'Failed to load user');
        } finally {
            setLoading(false);
        }
        fetchWallet();
    };

    const fetchWallet = async () => {
        try {
            const token = localStorage.getItem('token');
            const response = await axios.get(`${CONFIG.apiUrl}/admin/users/${id}/wallet`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            setWalletBalance(response.data.walletBalance || 0);
            setWalletTransactions(response.data.walletTransactions || []);
            setWalletWithdrawalRequests(response.data.walletWithdrawalRequests || []);
            setWalletOwnerEmail(response.data.walletOwnerEmail || '');
        } catch (error) {
            console.error('Error fetching wallet:', error);
        }
    };

    const handleWalletSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setWalletError('');
        setWalletSuccess('');
        const parsed = parseFloat(walletAmount);
        if (isNaN(parsed) || parsed === 0) {
            setWalletError('Montant invalide');
            return;
        }
        try {
            setWalletSaving(true);
            const token = localStorage.getItem('token');
            const response = await axios.post(
                `${CONFIG.apiUrl}/admin/users/${id}/wallet`,
                { amount: parsed, reason: walletReason },
                { headers: { Authorization: `Bearer ${token}` } }
            );
            setWalletBalance(response.data.walletBalance);
            setWalletOwnerEmail(response.data.walletOwnerEmail || '');
            setWalletAmount('');
            setWalletReason('');
            setWalletSuccess('Portefeuille mis à jour');
            fetchWallet();
        } catch (error: any) {
            setWalletError(error.response?.data?.message || 'Erreur lors de la mise à jour');
        } finally {
            setWalletSaving(false);
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        
        if (!formData.email.trim()) {
            setError('Email is required');
            return;
        }

        try {
            setSaving(true);
            setError('');
            const token = localStorage.getItem('token');
            
            await axios.patch(`${CONFIG.apiUrl}/admin/users/${id}`, formData, {
                headers: { Authorization: `Bearer ${token}` }
            });
            
            navigate('/back-office/users');
        } catch (error: any) {
            console.error('Error saving user:', error);
            setError(error.response?.data?.message || 'Failed to save user');
        } finally {
            setSaving(false);
        }
    };

    const handleChange = (field: keyof UserFormData, value: any) => {
        setFormData({ ...formData, [field]: value });
    };

    const toggleChannel = (channel: string) => {
        const current = formData.channels || [];
        const next = current.includes(channel)
            ? current.filter((c) => c !== channel)
            : [...current, channel];
        handleChange('channels', next);
    };

    const handleBlockToggle = async () => {
        setBlockError('');
        setBlockSuccess('');
        try {
            setBlockSaving(true);
            const token = localStorage.getItem('token');
            const action = isBlocked ? 'unblock' : 'block';
            await axios.post(`${CONFIG.apiUrl}/admin/users/${id}/${action}`, {}, {
                headers: { Authorization: `Bearer ${token}` },
            });
            setIsBlocked(!isBlocked);
            if (isBlocked) setIsLoginLocked(false);
            setBlockSuccess(isBlocked ? 'Account unblocked.' : 'Account blocked.');
        } catch (err: any) {
            setBlockError(err.response?.data?.message || 'Error updating account status.');
        } finally {
            setBlockSaving(false);
        }
    };

    const handleUnlockLogin = async () => {
        setBlockError('');
        setBlockSuccess('');
        try {
            setUnlockSaving(true);
            const token = localStorage.getItem('token');
            await axios.post(`${CONFIG.apiUrl}/admin/users/${id}/unlock-login`, {}, {
                headers: { Authorization: `Bearer ${token}` },
            });
            setIsLoginLocked(false);
            setBlockSuccess('Login lock removed.');
        } catch (err: any) {
            setBlockError(err.response?.data?.message || 'Error unlocking account.');
        } finally {
            setUnlockSaving(false);
        }
    };

    const handleResetPassword = async () => {
        setResetPasswordError('');
        setResetPasswordSuccess('');
        try {
            setResetPasswordSaving(true);
            const token = localStorage.getItem('token');
            await axios.post(`${CONFIG.apiUrl}/admin/users/${id}/reset-password`, {}, {
                headers: { Authorization: `Bearer ${token}` },
            });
            setResetPasswordSuccess('Nouveau mot de passe généré et envoyé sur Slack.');
            setShowResetPasswordConfirm(false);
        } catch (err: any) {
            setResetPasswordError(err.response?.data?.message || 'Erreur lors de la réinitialisation du mot de passe.');
        } finally {
            setResetPasswordSaving(false);
        }
    };

    const fetchBlockedIps = async () => {
        try {
            const token = localStorage.getItem('token');
            const response = await axios.get(`${CONFIG.apiUrl}/admin/ip-blacklist`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            setBlockedIps(new Set(response.data.map((e: any) => e.ip)));
        } catch (error) {
            console.error('Error fetching IP blacklist:', error);
        }
    };

    const handleIpBlock = async (ip: string) => {
        setIpActionLoading((prev) => new Set(prev).add(ip));
        try {
            const token = localStorage.getItem('token');
            await axios.post(
                `${CONFIG.apiUrl}/admin/ip-blacklist`,
                { ip, reason: `Blocked from user IP history (${formData.email})` },
                { headers: { Authorization: `Bearer ${token}` } }
            );
            setBlockedIps((prev) => new Set(prev).add(ip));
        } catch (error) {
            console.error('Error blocking IP:', error);
        } finally {
            setIpActionLoading((prev) => { const s = new Set(prev); s.delete(ip); return s; });
        }
    };

    const handleIpUnblock = async (ip: string) => {
        setIpActionLoading((prev) => new Set(prev).add(ip));
        try {
            const token = localStorage.getItem('token');
            await axios.delete(`${CONFIG.apiUrl}/admin/ip-blacklist/${encodeURIComponent(ip)}`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            setBlockedIps((prev) => { const s = new Set(prev); s.delete(ip); return s; });
        } catch (error) {
            console.error('Error unblocking IP:', error);
        } finally {
            setIpActionLoading((prev) => { const s = new Set(prev); s.delete(ip); return s; });
        }
    };

    if (loading) {
        return (
            <div className="pt-[50px]">
                <Card extra="p-5">
                    <div className="py-10 text-center text-gray-500">Loading...</div>
                </Card>
            </div>
        );
    }

    return (
        <div className="pt-[50px]">
            <Card extra="p-5">
                <div className="mb-5 flex items-center justify-between gap-3">
                    <div className="flex items-center gap-3">
                        <button
                            onClick={() => navigate('/back-office/users')}
                            className="rounded-lg bg-gray-200 p-2 text-gray-700 hover:bg-gray-300 dark:bg-navy-700 dark:text-white dark:hover:bg-navy-600"
                        >
                            <MdArrowBack className="text-xl" />
                        </button>
                        <div className="text-2xl font-bold text-navy-700 dark:text-white">
                            Edit User
                        </div>
                    </div>
                    <div className="flex items-center gap-2">
                        {stripeAccountId && (
                            <a
                                href={`https://dashboard.stripe.com/acct_1SZSD1PsZkDtRF8o/connect/accounts/${stripeAccountId}/activity`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="flex items-center gap-2 rounded-lg bg-indigo-500 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-600"
                            >
                                <MdOpenInNew />
                                Stripe Account
                            </a>
                        )}
                        {formData.email && (
                            <button
                                type="button"
                                onClick={() => navigate(`/back-office/leads?search=${encodeURIComponent(formData.email)}`)}
                                className="flex items-center gap-2 rounded-lg bg-brand-500 px-4 py-2 text-sm font-medium text-white hover:bg-brand-600"
                            >
                                <MdSearch />
                                Voir les leads
                            </button>
                        )}
                    </div>
                </div>

                {error && (
                    <div className="mb-4 rounded-lg bg-red-100 p-4 text-red-700 dark:bg-red-900/30 dark:text-red-400">
                        {error}
                    </div>
                )}

                <form onSubmit={handleSubmit}>
                    {/* Basic Information */}
                    <div className="mb-6">
                        <h3 className="mb-3 text-lg font-semibold text-navy-700 dark:text-white">
                            Basic Information
                        </h3>
                        
                        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                            <div>
                                <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
                                    First Name
                                </label>
                                <input
                                    type="text"
                                    value={formData.firstName}
                                    onChange={(e) => handleChange('firstName', e.target.value)}
                                    className="w-full rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm focus:border-brand-500 focus:outline-none dark:border-navy-600 dark:bg-navy-800 dark:text-white"
                                />
                            </div>

                            <div>
                                <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
                                    Last Name
                                </label>
                                <input
                                    type="text"
                                    value={formData.lastName}
                                    onChange={(e) => handleChange('lastName', e.target.value)}
                                    className="w-full rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm focus:border-brand-500 focus:outline-none dark:border-navy-600 dark:bg-navy-800 dark:text-white"
                                />
                            </div>

                            <div>
                                <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
                                    Email *
                                </label>
                                <input
                                    type="email"
                                    value={formData.email}
                                    onChange={(e) => handleChange('email', e.target.value)}
                                    className="w-full rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm focus:border-brand-500 focus:outline-none dark:border-navy-600 dark:bg-navy-800 dark:text-white"
                                    required
                                />
                            </div>

                            <div>
                                <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
                                    Phone
                                </label>
                                <input
                                    type="tel"
                                    value={formData.phone}
                                    onChange={(e) => handleChange('phone', e.target.value)}
                                    className="w-full rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm focus:border-brand-500 focus:outline-none dark:border-navy-600 dark:bg-navy-800 dark:text-white"
                                />
                            </div>

                            <div>
                                <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
                                    Code d'affiliation
                                </label>
                                <select
                                    value={formData.referal}
                                    onChange={(e) => handleChange('referal', e.target.value)}
                                    className="w-full rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm focus:border-brand-500 focus:outline-none dark:border-navy-600 dark:bg-navy-800 dark:text-white"
                                >
                                    <option value="">— Aucun —</option>
                                    {coupons.map((c) => (
                                        <option key={c._id} value={c.coupon}>
                                            {c.coupon} ({c.type}, {c.fixedValue}€)
                                        </option>
                                    ))}
                                </select>
                            </div>
                        </div>
                    </div>

                    {/* User Type & Role */}
                    <div className="mb-6">
                        <h3 className="mb-3 text-lg font-semibold text-navy-700 dark:text-white">
                            User Type & Role
                        </h3>
                        
                        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                            <div>
                                <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
                                    Type *
                                </label>
                                <select
                                    value={formData.type}
                                    onChange={(e) => handleChange('type', e.target.value as 'buyer' | 'seller')}
                                    className="w-full rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm focus:border-brand-500 focus:outline-none dark:border-navy-600 dark:bg-navy-800 dark:text-white"
                                    required
                                >
                                    <option value="buyer">Buyer</option>
                                    <option value="seller">Seller</option>
                                </select>
                            </div>

                            <div>
                                <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
                                    Role
                                </label>
                                <select
                                    value={formData.role}
                                    onChange={(e) => handleChange('role', e.target.value)}
                                    className="w-full rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm focus:border-brand-500 focus:outline-none dark:border-navy-600 dark:bg-navy-800 dark:text-white"
                                >
                                    <option value="">Regular User</option>
                                    <option value="admin">Admin</option>
                                </select>
                            </div>
                        </div>
                    </div>

                    {/* Company Information */}
                    <div className="mb-6">
                        <h3 className="mb-3 text-lg font-semibold text-navy-700 dark:text-white">
                            Company Information
                        </h3>
                        
                        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                            <div>
                                <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
                                    Company
                                </label>
                                <input
                                    type="text"
                                    value={formData.company}
                                    onChange={(e) => handleChange('company', e.target.value)}
                                    className="w-full rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm focus:border-brand-500 focus:outline-none dark:border-navy-600 dark:bg-navy-800 dark:text-white"
                                />
                            </div>

                            <div>
                                <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
                                    Website
                                </label>
                                <input
                                    type="url"
                                    value={formData.website}
                                    onChange={(e) => handleChange('website', e.target.value)}
                                    className="w-full rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm focus:border-brand-500 focus:outline-none dark:border-navy-600 dark:bg-navy-800 dark:text-white"
                                />
                            </div>

                            <div className="md:col-span-2">
                                <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
                                    Address
                                </label>
                                <input
                                    type="text"
                                    value={formData.address}
                                    onChange={(e) => handleChange('address', e.target.value)}
                                    className="w-full rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm focus:border-brand-500 focus:outline-none dark:border-navy-600 dark:bg-navy-800 dark:text-white"
                                />
                            </div>

                            <div>
                                <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
                                    VAT Number
                                </label>
                                <input
                                    type="text"
                                    value={formData.vatNumber}
                                    onChange={(e) => handleChange('vatNumber', e.target.value)}
                                    className="w-full rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm focus:border-brand-500 focus:outline-none dark:border-navy-600 dark:bg-navy-800 dark:text-white"
                                />
                            </div>

                            <div>
                                <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
                                    SIREN
                                </label>
                                <input
                                    type="text"
                                    value={formData.siren}
                                    onChange={(e) => handleChange('siren', e.target.value)}
                                    className="w-full rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm focus:border-brand-500 focus:outline-none dark:border-navy-600 dark:bg-navy-800 dark:text-white"
                                />
                            </div>

                            <div>
                                <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
                                    SIRET
                                </label>
                                <input
                                    type="text"
                                    value={formData.siret}
                                    onChange={(e) => handleChange('siret', e.target.value)}
                                    className="w-full rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm focus:border-brand-500 focus:outline-none dark:border-navy-600 dark:bg-navy-800 dark:text-white"
                                />
                            </div>
                        </div>
                    </div>

                    {/* Verification Status */}
                    <div className="mb-6">
                        <h3 className="mb-3 text-lg font-semibold text-navy-700 dark:text-white">
                            Verification Status
                        </h3>

                        <div className="space-y-3">
                            <label className="flex items-center gap-3">
                                <input
                                    type="checkbox"
                                    checked={formData.isValidated}
                                    onChange={(e) => handleChange('isValidated', e.target.checked)}
                                    className="h-4 w-4 rounded border-gray-300 text-brand-500 focus:ring-brand-500"
                                />
                                <span className="text-sm text-gray-700 dark:text-gray-300">
                                    Account Validated
                                </span>
                            </label>

                            <label className="flex items-center gap-3">
                                <input
                                    type="checkbox"
                                    checked={formData.isKYCVerified}
                                    onChange={(e) => handleChange('isKYCVerified', e.target.checked)}
                                    className="h-4 w-4 rounded border-gray-300 text-brand-500 focus:ring-brand-500"
                                />
                                <span className="text-sm text-gray-700 dark:text-gray-300">
                                    KYC Verified
                                </span>
                            </label>

                            <label className="flex items-center gap-3">
                                <input
                                    type="checkbox"
                                    checked={formData.forceKYCVerification}
                                    onChange={(e) => handleChange('forceKYCVerification', e.target.checked)}
                                    className="h-4 w-4 rounded border-gray-300 text-orange-500 focus:ring-orange-500"
                                />
                                <span className="text-sm text-gray-700 dark:text-gray-300">
                                    Forcer la vérification KYC
                                </span>
                            </label>

                            <label className="flex items-center gap-3">
                                <input
                                    type="checkbox"
                                    checked={formData.canSwitchAccountType}
                                    onChange={(e) => handleChange('canSwitchAccountType', e.target.checked)}
                                    className="h-4 w-4 rounded border-gray-300 text-brand-500 focus:ring-brand-500"
                                />
                                <span className="text-sm text-gray-700 dark:text-gray-300">
                                    Autoriser le bridge acheteur/vendeur (l'utilisateur peut créer
                                    et basculer vers un compte {formData.type === 'seller' ? 'acheteur' : 'vendeur'} lié)
                                </span>
                            </label>

                            {formData.type === 'seller' && (
                                <div className="space-y-4">
                                    <div>
                                        <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
                                            Commission (%)
                                        </label>
                                        <input
                                            type="number"
                                            min="0"
                                            max="100"
                                            step="0.1"
                                            value={formData.commission}
                                            onChange={(e) => handleChange('commission', parseFloat(e.target.value))}
                                            className="w-32 rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm focus:border-brand-500 focus:outline-none dark:border-navy-600 dark:bg-navy-800 dark:text-white"
                                        />
                                        <p className="mt-1 text-xs text-gray-500">Défaut : 15%. La commission est ajoutée au prix vendeur pour calculer le prix acheteur.</p>
                                    </div>
                                    <div>
                                        <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
                                            Prix fixe (€) <span className="text-gray-400 font-normal">Note: désactive la commission %</span>
                                        </label>
                                        <div className="flex items-center gap-3">
                                            <input
                                                type="number"
                                                min="0"
                                                step="0.01"
                                                value={formData.commissionFixedPrice ?? ''}
                                                placeholder="Non défini"
                                                onChange={(e) => handleChange('commissionFixedPrice', e.target.value === '' ? null : parseFloat(e.target.value))}
                                                className="w-32 rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm focus:border-brand-500 focus:outline-none dark:border-navy-600 dark:bg-navy-800 dark:text-white"
                                            />
                                            {formData.commissionFixedPrice != null && (
                                                <button
                                                    type="button"
                                                    onClick={() => handleChange('commissionFixedPrice', null)}
                                                    className="text-xs text-red-500 hover:underline"
                                                >
                                                    Supprimer
                                                </button>
                                            )}
                                        </div>
                                        <p className="mt-1 text-xs text-gray-500">Si défini, ce montant fixe est ajouté au prix vendeur à la place de la commission %.</p>
                                    </div>
                                    <div>
                                        <span className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
                                            Méthode de paiement
                                        </span>
                                        <div className="flex gap-2">
                                            <button
                                                type="button"
                                                onClick={() => handleChange('paymentMethod', 'stripe')}
                                                className={`rounded-full px-4 py-1.5 text-sm font-semibold transition-colors ${
                                                    (formData.paymentMethod ?? 'stripe') === 'stripe'
                                                        ? 'bg-brand-500 text-white'
                                                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200 dark:bg-navy-700 dark:text-gray-300 dark:hover:bg-navy-600'
                                                }`}
                                            >
                                                Stripe
                                            </button>
                                            <button
                                                type="button"
                                                onClick={() => handleChange('paymentMethod', 'cagnotte')}
                                                className={`rounded-full px-4 py-1.5 text-sm font-semibold transition-colors ${
                                                    formData.paymentMethod === 'cagnotte'
                                                        ? 'bg-brand-500 text-white'
                                                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200 dark:bg-navy-700 dark:text-gray-300 dark:hover:bg-navy-600'
                                                }`}
                                            >
                                                Cagnotte
                                            </button>
                                        </div>
                                        <p className="mt-1 text-xs text-gray-500">
                                            Stripe : virement immédiat au paiement d'un lead. Cagnotte : le montant est crédité sur un solde interne avec historique, sans virement Stripe.
                                        </p>
                                        {formData.canSwitchAccountType && formData.paymentMethod === 'cagnotte' && (
                                            <p className="mt-1 text-xs font-medium text-brand-600">
                                                Bridge actif{linkedAccount ? ` vers ${linkedAccount.email}` : ''} : en mode
                                                Cagnotte, les paiements de leads sont crédités directement sur le
                                                portefeuille du compte acheteur lié (section "Portefeuille" de ce compte),
                                                pas sur la Cagnotte ci-dessous.
                                            </p>
                                        )}
                                    </div>
                                    <div>
                                        <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
                                            Solde pré-payé (€)
                                        </label>
                                        <input
                                            type="number"
                                            min="0"
                                            step="0.01"
                                            value={formData.prePaidBalance ?? 0}
                                            onChange={(e) => handleChange('prePaidBalance', parseFloat(e.target.value) || 0)}
                                            className="w-32 rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm focus:border-brand-500 focus:outline-none dark:border-navy-600 dark:bg-navy-800 dark:text-white"
                                        />
                                        <p className="mt-1 text-xs text-gray-500">
                                            Montant déjà versé au vendeur hors Stripe. Déduit automatiquement lors du prochain paiement de lead.
                                        </p>
                                    </div>
                                    <div>
                                        <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
                                            Montant déjà payé (€)
                                        </label>
                                        <input
                                            type="number"
                                            min="0"
                                            step="0.01"
                                            value={formData.alreadyPaidAmount ?? 0}
                                            onChange={(e) => handleChange('alreadyPaidAmount', parseFloat(e.target.value) || 0)}
                                            className="w-32 rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm focus:border-brand-500 focus:outline-none dark:border-navy-600 dark:bg-navy-800 dark:text-white"
                                        />
                                        <p className="mt-1 text-xs text-gray-500">
                                            Avance manuelle versée au vendeur. Le bouton "Payer" est remplacé par "Marquer payé" jusqu'à épuisement.
                                        </p>
                                    </div>
                                    <div>
                                        <span className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
                                            Canaux d'acquisition
                                        </span>
                                        <div className="flex flex-wrap gap-2">
                                            {allChannels.map((channel) => {
                                                const isSelected = (formData.channels || []).includes(channel);
                                                return (
                                                    <button
                                                        key={channel}
                                                        type="button"
                                                        onClick={() => toggleChannel(channel)}
                                                        className={`rounded-full px-3 py-1 text-xs font-semibold transition-colors ${
                                                            isSelected
                                                                ? 'bg-brand-500 text-white'
                                                                : 'bg-gray-100 text-gray-600 hover:bg-gray-200 dark:bg-navy-700 dark:text-gray-300 dark:hover:bg-navy-600'
                                                        }`}
                                                    >
                                                        {channel}
                                                    </button>
                                                );
                                            })}
                                        </div>
                                    </div>
                                    <div>
                                    <span className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
                                        Niveau vendeur
                                    </span>
                                    <div className="flex gap-2">
                                        <button
                                            type="button"
                                            onClick={() => setFormData({ ...formData, blockedSeller: true, canSellWithGSheet: false })}
                                            className={`rounded-full px-4 py-1.5 text-sm font-semibold transition-colors ${
                                                formData.blockedSeller
                                                    ? 'bg-red-500 text-white'
                                                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200 dark:bg-navy-700 dark:text-gray-300 dark:hover:bg-navy-600'
                                            }`}
                                        >
                                            Vendeur -
                                        </button>
                                        <button
                                            type="button"
                                            onClick={() => setFormData({ ...formData, blockedSeller: false, canSellWithGSheet: false })}
                                            className={`rounded-full px-4 py-1.5 text-sm font-semibold transition-colors ${
                                                !formData.blockedSeller && !formData.canSellWithGSheet
                                                    ? 'bg-blue-500 text-white'
                                                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200 dark:bg-navy-700 dark:text-gray-300 dark:hover:bg-navy-600'
                                            }`}
                                        >
                                            Vendeur +
                                        </button>
                                        <button
                                            type="button"
                                            onClick={() => setFormData({ ...formData, blockedSeller: false, canSellWithGSheet: true })}
                                            className={`rounded-full px-4 py-1.5 text-sm font-semibold transition-colors ${
                                                formData.canSellWithGSheet && !formData.blockedSeller
                                                    ? 'bg-green-500 text-white'
                                                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200 dark:bg-navy-700 dark:text-gray-300 dark:hover:bg-navy-600'
                                            }`}
                                        >
                                            Vendeur ++
                                        </button>
                                    </div>
                                    </div>

                                    <div>
                                        <span className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
                                            Installation du pixel
                                        </span>
                                        <div className="flex flex-wrap gap-2">
                                            {([
                                                { value: '', label: 'Défaut (global)' },
                                                { value: 'mandatory', label: 'Obligatoire' },
                                                { value: 'optional', label: 'Optionnel' },
                                                { value: 'hidden', label: 'Caché' },
                                            ] as const).map((opt) => (
                                                <button
                                                    key={opt.value || 'default'}
                                                    type="button"
                                                    onClick={() => handleChange('pixelModeOverride', opt.value)}
                                                    className={`rounded-full px-4 py-1.5 text-sm font-semibold transition-colors ${
                                                        (formData.pixelModeOverride ?? '') === opt.value
                                                            ? 'bg-brand-500 text-white'
                                                            : 'bg-gray-100 text-gray-600 hover:bg-gray-200 dark:bg-navy-700 dark:text-gray-300 dark:hover:bg-navy-600'
                                                    }`}
                                                >
                                                    {opt.label}
                                                </button>
                                            ))}
                                        </div>
                                        <p className="mt-1 text-xs text-gray-500">
                                            Surcharge le mode d'installation du pixel défini globalement dans Back Office &gt; Pixels, uniquement pour ce vendeur. « Défaut (global) » = suit le réglage global.
                                        </p>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Action Buttons */}
                    <div className="flex justify-end gap-3 border-t border-gray-200 pt-4 dark:border-navy-600">
                        <button
                            type="button"
                            onClick={() => navigate('/back-office/users')}
                            className="rounded-lg border border-gray-300 bg-white px-6 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 dark:border-navy-600 dark:bg-navy-800 dark:text-white dark:hover:bg-navy-700"
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            disabled={saving}
                            className="flex items-center gap-2 rounded-lg bg-brand-500 px-6 py-2 text-sm font-medium text-white hover:bg-brand-600 disabled:cursor-not-allowed disabled:opacity-50"
                        >
                            <MdSave />
                            {saving ? 'Saving...' : 'Save Changes'}
                        </button>
                    </div>
                </form>

                {/* Account Security */}
                <div className="mt-8 border-t border-gray-200 pt-6 dark:border-navy-600">
                    <h3 className="mb-4 flex items-center gap-2 text-lg font-semibold text-navy-700 dark:text-white">
                        <MdBlock className="text-red-500" />
                        Sécurité du compte
                    </h3>

                    <div className="mb-4 space-y-2">
                        <div className={`flex items-center gap-3 rounded-lg px-4 py-3 ${isBlocked ? 'bg-red-50 dark:bg-red-900/20' : 'bg-green-50 dark:bg-green-900/20'}`}>
                            <span className={`h-3 w-3 rounded-full ${isBlocked ? 'bg-red-500' : 'bg-green-500'}`} />
                            <span className={`text-sm font-medium ${isBlocked ? 'text-red-700 dark:text-red-400' : 'text-green-700 dark:text-green-400'}`}>
                                {isBlocked ? 'Compte bloqué — login et requêtes API refusés' : 'Compte actif'}
                            </span>
                        </div>
                        {isLoginLocked && (
                            <div className="flex items-center gap-3 rounded-lg bg-orange-50 px-4 py-3 dark:bg-orange-900/20">
                                <span className="h-3 w-3 rounded-full bg-orange-500" />
                                <span className="text-sm font-medium text-orange-700 dark:text-orange-400">
                                    Login verrouillé — trop de tentatives échouées
                                </span>
                            </div>
                        )}
                    </div>

                    <div className="flex flex-wrap gap-2">
                        <button
                            type="button"
                            onClick={handleBlockToggle}
                            disabled={blockSaving}
                            className={`flex items-center gap-2 rounded-lg px-5 py-2 text-sm font-medium text-white disabled:opacity-50 ${
                                isBlocked
                                    ? 'bg-green-600 hover:bg-green-700'
                                    : 'bg-red-600 hover:bg-red-700'
                            }`}
                        >
                            {isBlocked ? <MdLockOpen /> : <MdBlock />}
                            {blockSaving ? 'En cours...' : isBlocked ? 'Débloquer le compte' : 'Bloquer le compte'}
                        </button>
                        {isLoginLocked && (
                            <button
                                type="button"
                                onClick={handleUnlockLogin}
                                disabled={unlockSaving}
                                className="flex items-center gap-2 rounded-lg bg-orange-500 px-5 py-2 text-sm font-medium text-white hover:bg-orange-600 disabled:opacity-50"
                            >
                                <MdLockOpen />
                                {unlockSaving ? 'En cours...' : 'Déverrouiller le login'}
                            </button>
                        )}
                        <button
                            type="button"
                            onClick={() => setShowResetPasswordConfirm(true)}
                            className="flex items-center gap-2 rounded-lg bg-navy-700 px-5 py-2 text-sm font-medium text-white hover:bg-navy-800"
                        >
                            <MdVpnKey />
                            Réinitialiser le mot de passe
                        </button>
                    </div>

                    {blockError && (
                        <div className="mt-3 rounded-lg bg-red-100 p-3 text-sm text-red-700 dark:bg-red-900/30 dark:text-red-400">
                            {blockError}
                        </div>
                    )}
                    {blockSuccess && (
                        <div className="mt-3 rounded-lg bg-green-100 p-3 text-sm text-green-700 dark:bg-green-900/30 dark:text-green-400">
                            {blockSuccess}
                        </div>
                    )}
                    {resetPasswordError && (
                        <div className="mt-3 rounded-lg bg-red-100 p-3 text-sm text-red-700 dark:bg-red-900/30 dark:text-red-400">
                            {resetPasswordError}
                        </div>
                    )}
                    {resetPasswordSuccess && (
                        <div className="mt-3 rounded-lg bg-green-100 p-3 text-sm text-green-700 dark:bg-green-900/30 dark:text-green-400">
                            {resetPasswordSuccess}
                        </div>
                    )}
                </div>

                {showResetPasswordConfirm && (
                    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
                        <div className="w-full max-w-md rounded-xl bg-white p-6 shadow-xl dark:bg-navy-800">
                            <h4 className="mb-2 flex items-center gap-2 text-lg font-semibold text-navy-700 dark:text-white">
                                <MdVpnKey className="text-navy-700 dark:text-white" />
                                Réinitialiser le mot de passe ?
                            </h4>
                            <p className="mb-5 text-sm text-gray-600 dark:text-gray-300">
                                Un nouveau mot de passe va être généré pour <span className="font-semibold">{formData.email}</span> et
                                envoyé sur le canal Slack sécurité. L'ancien mot de passe cessera de fonctionner immédiatement.
                            </p>
                            <div className="flex justify-end gap-3">
                                <button
                                    type="button"
                                    onClick={() => setShowResetPasswordConfirm(false)}
                                    disabled={resetPasswordSaving}
                                    className="rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 dark:border-navy-600 dark:bg-navy-800 dark:text-white dark:hover:bg-navy-700"
                                >
                                    Annuler
                                </button>
                                <button
                                    type="button"
                                    onClick={handleResetPassword}
                                    disabled={resetPasswordSaving}
                                    className="flex items-center gap-2 rounded-lg bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-700 disabled:opacity-50"
                                >
                                    <MdVpnKey />
                                    {resetPasswordSaving ? 'Génération...' : 'Confirmer'}
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                {/* IP History */}
                <div className="mt-8 border-t border-gray-200 pt-6 dark:border-navy-600">
                    <h3 className="mb-4 flex items-center gap-2 text-lg font-semibold text-navy-700 dark:text-white">
                        <MdLanguage className="text-brand-500" />
                        IP History
                    </h3>

                    {ipHistory.length === 0 ? (
                        <p className="text-sm text-gray-400">No IP recorded yet.</p>
                    ) : (
                        <div className="overflow-x-auto">
                            <table className="w-full text-sm">
                                <thead>
                                    <tr className="border-b border-gray-200 dark:border-navy-600">
                                        <th className="pb-2 text-left text-xs font-bold uppercase text-gray-500">Country</th>
                                        <th className="pb-2 text-left text-xs font-bold uppercase text-gray-500">IP</th>
                                        <th className="pb-2 text-left text-xs font-bold uppercase text-gray-500">First Seen</th>
                                        <th className="pb-2 text-left text-xs font-bold uppercase text-gray-500">Status</th>
                                        <th className="pb-2 text-left text-xs font-bold uppercase text-gray-500">Action</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {[...ipHistory].reverse().map((entry, i) => {
                                        const blocked = blockedIps.has(entry.ip);
                                        const loading = ipActionLoading.has(entry.ip);
                                        return (
                                            <tr key={i} className={`border-b border-gray-100 dark:border-navy-700 ${blocked ? 'bg-red-50 dark:bg-red-900/10' : ''}`}>
                                                <td className="py-2 text-gray-700 dark:text-gray-300">
                                                    {entry.country || '—'}
                                                </td>
                                                <td className="py-2 font-mono text-gray-600 dark:text-gray-400">
                                                    {entry.ip}
                                                </td>
                                                <td className="py-2 text-gray-500">
                                                    {new Date(entry.firstSeenAt).toLocaleString('fr-FR')}
                                                </td>
                                                <td className="py-2">
                                                    <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-semibold ${blocked ? 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400' : 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'}`}>
                                                        {blocked ? 'Blocked' : 'Active'}
                                                    </span>
                                                </td>
                                                <td className="py-2">
                                                    {blocked ? (
                                                        <button
                                                            onClick={() => handleIpUnblock(entry.ip)}
                                                            disabled={loading}
                                                            className="flex items-center gap-1 rounded-lg bg-green-600 px-3 py-1 text-xs font-medium text-white hover:bg-green-700 disabled:opacity-50"
                                                        >
                                                            <MdLockOpen className="text-sm" />
                                                            {loading ? '...' : 'Unblock'}
                                                        </button>
                                                    ) : (
                                                        <button
                                                            onClick={() => handleIpBlock(entry.ip)}
                                                            disabled={loading}
                                                            className="flex items-center gap-1 rounded-lg bg-red-600 px-3 py-1 text-xs font-medium text-white hover:bg-red-700 disabled:opacity-50"
                                                        >
                                                            <MdBlock className="text-sm" />
                                                            {loading ? '...' : 'Block'}
                                                        </button>
                                                    )}
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>

                {/* Wallet Management (buyers, and sellers paid via a linked buyer's wallet) */}
                {(userType === 'buyer' ||
                    (userType === 'seller' &&
                        formData.canSwitchAccountType &&
                        formData.paymentMethod === 'cagnotte' &&
                        linkedAccount?.type === 'buyer')) && (
                    <div className="mt-8 border-t border-gray-200 pt-6 dark:border-navy-600">
                        <h3 className="mb-4 flex items-center gap-2 text-lg font-semibold text-navy-700 dark:text-white">
                            <MdAccountBalanceWallet className="text-brand-500" />
                            Portefeuille
                        </h3>

                        {userType === 'seller' && (
                            <p className="mb-4 text-xs font-medium text-brand-600">
                                Ce vendeur est payé en mode Cagnotte et lié à un compte acheteur
                                {walletOwnerEmail ? ` (${walletOwnerEmail})` : ''} : ses paiements de leads sont
                                crédités directement sur ce portefeuille.
                            </p>
                        )}

                        {userType === 'buyer' && formData.canSwitchAccountType && linkedAccount?.type === 'seller' && (
                            <p className="mb-4 text-xs font-medium text-brand-600">
                                Bridge actif vers {linkedAccount.email} (vendeur) : si ce vendeur est en mode de
                                paiement Cagnotte, ses paiements de leads sont crédités automatiquement ici.
                            </p>
                        )}

                        <div className="mb-4 flex items-center gap-3">
                            <span className="text-sm text-gray-600 dark:text-gray-400">Solde actuel :</span>
                            <span className="text-xl font-bold text-navy-700 dark:text-white">
                                {walletBalance.toFixed(2)} €
                            </span>
                        </div>

                        <form onSubmit={handleWalletSubmit} className="mb-6 flex flex-wrap items-end gap-3">
                            <div>
                                <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
                                    Montant (€) <span className="text-gray-400 text-xs">négatif pour retirer</span>
                                </label>
                                <input
                                    type="number"
                                    step="0.01"
                                    value={walletAmount}
                                    onChange={(e) => setWalletAmount(e.target.value)}
                                    placeholder="ex: 50 ou -20"
                                    className="w-40 rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm focus:border-brand-500 focus:outline-none dark:border-navy-600 dark:bg-navy-800 dark:text-white"
                                />
                            </div>
                            <div className="flex-1">
                                <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
                                    Raison
                                </label>
                                <input
                                    type="text"
                                    value={walletReason}
                                    onChange={(e) => setWalletReason(e.target.value)}
                                    placeholder="ex: Remboursement, crédit offert..."
                                    className="w-full rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm focus:border-brand-500 focus:outline-none dark:border-navy-600 dark:bg-navy-800 dark:text-white"
                                />
                            </div>
                            <button
                                type="submit"
                                disabled={walletSaving}
                                className="flex items-center gap-2 rounded-lg bg-brand-500 px-4 py-2 text-sm font-medium text-white hover:bg-brand-600 disabled:opacity-50"
                            >
                                <MdAccountBalanceWallet />
                                {walletSaving ? 'En cours...' : 'Appliquer'}
                            </button>
                        </form>

                        {walletError && (
                            <div className="mb-3 rounded-lg bg-red-100 p-3 text-sm text-red-700 dark:bg-red-900/30 dark:text-red-400">
                                {walletError}
                            </div>
                        )}
                        {walletSuccess && (
                            <div className="mb-3 rounded-lg bg-green-100 p-3 text-sm text-green-700 dark:bg-green-900/30 dark:text-green-400">
                                {walletSuccess}
                            </div>
                        )}

                        {walletTransactions.length > 0 && (
                            <div className="overflow-x-auto">
                                <p className="mb-2 text-sm font-medium text-gray-700 dark:text-gray-300">Historique</p>
                                <table className="w-full text-sm">
                                    <thead>
                                        <tr className="border-b border-gray-200 dark:border-navy-600">
                                            <th className="pb-2 text-left text-xs font-bold uppercase text-gray-500">Date</th>
                                            <th className="pb-2 text-left text-xs font-bold uppercase text-gray-500">Montant</th>
                                            <th className="pb-2 text-left text-xs font-bold uppercase text-gray-500">Raison</th>
                                            <th className="pb-2 text-left text-xs font-bold uppercase text-gray-500">Admin</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {walletTransactions.map((tx: any, i: number) => (
                                            <tr key={i} className="border-b border-gray-100 dark:border-navy-700">
                                                <td className="py-2 text-gray-600 dark:text-gray-400">
                                                    {new Date(tx.createdAt).toLocaleDateString('fr-FR')}
                                                </td>
                                                <td className={`py-2 font-semibold ${tx.amount >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                                                    {tx.amount >= 0 ? '+' : ''}{tx.amount.toFixed(2)} €
                                                </td>
                                                <td className="py-2 text-gray-600 dark:text-gray-400">{tx.reason || '-'}</td>
                                                <td className="py-2 text-gray-500">{tx.adminEmail || '-'}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}

                        {walletWithdrawalRequests.length > 0 && (
                            <div className="mt-6 overflow-x-auto">
                                <p className="mb-2 text-sm font-medium text-gray-700 dark:text-gray-300">
                                    Demandes de retrait
                                </p>
                                <table className="w-full text-sm">
                                    <thead>
                                        <tr className="border-b border-gray-200 dark:border-navy-600">
                                            <th className="pb-2 text-left text-xs font-bold uppercase text-gray-500">Date</th>
                                            <th className="pb-2 text-left text-xs font-bold uppercase text-gray-500">Montant</th>
                                            <th className="pb-2 text-left text-xs font-bold uppercase text-gray-500">Statut</th>
                                            <th className="pb-2 text-left text-xs font-bold uppercase text-gray-500">Traité par</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {walletWithdrawalRequests.map((r: any, i: number) => (
                                            <tr key={i} className="border-b border-gray-100 dark:border-navy-700">
                                                <td className="py-2 text-gray-600 dark:text-gray-400">
                                                    {new Date(r.requestedAt).toLocaleDateString('fr-FR')}
                                                </td>
                                                <td className="py-2 font-semibold text-navy-700 dark:text-white">
                                                    {Number(r.amount).toFixed(2)} €
                                                </td>
                                                <td className="py-2 text-gray-600 dark:text-gray-400">
                                                    {r.status === 'pending'
                                                        ? 'En attente'
                                                        : r.status === 'sent'
                                                        ? 'Virement envoyé'
                                                        : 'Refusée'}
                                                    {r.status === 'rejected' && r.note ? ` — ${r.note}` : ''}
                                                </td>
                                                <td className="py-2 text-gray-500">{r.processedBy || '-'}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                                <p className="mt-2 text-xs text-gray-400">
                                    Le traitement se fait depuis la page « Retraits cagnotte » du back-office.
                                </p>
                            </div>
                        )}
                    </div>
                )}

                {/* Cagnotte (sellers only) */}
                {userType === 'seller' && (
                    <div className="mt-8 border-t border-gray-200 pt-6 dark:border-navy-600">
                        <h3 className="mb-4 flex items-center gap-2 text-lg font-semibold text-navy-700 dark:text-white">
                            <MdAccountBalanceWallet className="text-brand-500" />
                            Cagnotte
                        </h3>

                        {formData.canSwitchAccountType && linkedAccount?.type === 'buyer' && formData.paymentMethod === 'cagnotte' && (
                            <p className="mb-4 text-xs font-medium text-brand-600">
                                Bridge actif vers {linkedAccount.email} (acheteur) : les nouveaux paiements de leads en
                                mode Cagnotte sont désormais crédités sur le portefeuille de ce compte lié, pas sur le
                                solde ci-dessous.
                            </p>
                        )}

                        <div className="mb-4 flex items-center gap-3">
                            <span className="text-sm text-gray-600 dark:text-gray-400">Solde actuel :</span>
                            <span className="text-xl font-bold text-navy-700 dark:text-white">
                                {cagnotteBalance.toFixed(2)} €
                            </span>
                        </div>

                        {cagnotteTransactions.length > 0 ? (
                            <div className="overflow-x-auto">
                                <p className="mb-2 text-sm font-medium text-gray-700 dark:text-gray-300">Historique</p>
                                <table className="w-full text-sm">
                                    <thead>
                                        <tr className="border-b border-gray-200 dark:border-navy-600">
                                            <th className="pb-2 text-left text-xs font-bold uppercase text-gray-500">Date</th>
                                            <th className="pb-2 text-left text-xs font-bold uppercase text-gray-500">Montant</th>
                                            <th className="pb-2 text-left text-xs font-bold uppercase text-gray-500">Raison</th>
                                            <th className="pb-2 text-left text-xs font-bold uppercase text-gray-500">Lead</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {cagnotteTransactions.map((tx: any, i: number) => (
                                            <tr key={i} className="border-b border-gray-100 dark:border-navy-700">
                                                <td className="py-2 text-gray-600 dark:text-gray-400">
                                                    {new Date(tx.createdAt).toLocaleDateString('fr-FR')}
                                                </td>
                                                <td className="py-2 font-semibold text-green-600">
                                                    +{tx.amount.toFixed(2)} €
                                                </td>
                                                <td className="py-2 text-gray-600 dark:text-gray-400">{tx.reason || '-'}</td>
                                                <td className="py-2 text-gray-500">{tx.leadId || '-'}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        ) : (
                            <p className="text-sm text-gray-500">Aucune transaction pour le moment.</p>
                        )}
                    </div>
                )}
            </Card>
        </div>
    );
}
